import { Observable, of } from 'rxjs';

import { FeatureFlagsProvider } from './feature-flags-provider';

export class FeatureFlagsService<
	TFeatureFlagIds,
	FeatureFlags extends { id: TFeatureFlagIds; enabled?: boolean },
	ConfigType = object
> {
	public isLoading$ = this.dataProvider?.isLoading$ ?? of(false);

	constructor(
		private readonly featureFlags: readonly FeatureFlags[],
		private dataProvider?: FeatureFlagsProvider<TFeatureFlagIds, FeatureFlags, ConfigType>
	) {}

	/**
	 * Checks if a specific feature flag is enabled based on its ID.
	 *
	 * @remarks
	 * This function will likely be deprecated in the future. Users are advised to check for
	 * upcoming updates or alternatives before integrating this function into production environments.
	 */
	public isFeatureEnabled(id: TFeatureFlagIds): boolean {
		return this.enabledFeatureFlags().some((featureFlags) => featureFlags.id === id);
	}

	/**
	 * Checks if a specific feature flag is disabled based on its ID.
	 *
	 * @remarks
	 * This function will likely be deprecated in the future. Users are advised to check for
	 * upcoming updates or alternatives before integrating this function into production environments.
	 */
	public isFeatureDisabled(id: TFeatureFlagIds): boolean {
		return !this.isFeatureEnabled(id);
	}

	/**
	 * Retrieves an array of enabled feature flags.
	 *
	 * @remarks
	 * This function will likely be deprecated in the future. Users are advised to check for
	 * upcoming updates or alternatives before integrating this function into production environments.
	 */
	public enabledFeatureFlags(): FeatureFlags[] {
		return this.featureFlags.filter((featureFlags) => featureFlags.enabled);
	}

	/**
	 * Retrieves the status of a feature flag as an Observable boolean.
	 */
	public getFeatureFlag$(flagName: TFeatureFlagIds): Observable<boolean> {
		return this.dataProvider ? this.dataProvider.getFeatureFlag$(flagName) : of(false);
	}

	/**
	 * Retrieves the current configuration.
	 */
	public getConfig(): ConfigType {
		return this.dataProvider ? this.dataProvider.getConfig() : <ConfigType>{};
	}

	/**
	 * Sets the configuration as an Observable void.
	 */
	public setConfig$(config: ConfigType): Observable<void> {
		return this.dataProvider ? this.dataProvider.setConfig$(config) : of(undefined);
	}

	/**
	 * Refreshes the cache as an Observable void.
	 */
	public refreshCache$(): Observable<void> {
		return this.dataProvider ? this.dataProvider.refreshCache() : of(undefined);
	}

	public getDynamicConfig(flagName: TFeatureFlagIds): Observable<FeatureFlags> {
		return this.dataProvider ? this.dataProvider.getDynamicConfig$(flagName) : of(<FeatureFlags>{});
	}
}
